import CardFillDetailDialog from '@/components/client/card-fill-detail-dialog';
import {
	CardTreeBlock,
	EmptyPhoneCardsTree,
	PhoneSimHeader,
} from '@/components/client/client-card-tree-lines';
import { Button } from '@/components/ui/button';
import type { ICard, ICardFillJob, IClient, IPhone } from '@/entities/client.entity';
import { groupCardsByPhone, splitUnlinkedCards } from '@/lib/group-client-cards';
import { getPhoneStatusBorderClass } from '@/lib/phone-status-border';
import { socket } from '@/api/socket';
import { SOCKET_EVENTS } from '@/api/socket-events';
import { cn } from '@/lib/utils';
import { ChevronDownIcon, ChevronUpIcon, CreditCardIcon, PhoneIcon } from 'lucide-react';
import { useMemo, useState } from 'react';
import SendUssdModal from '@/pages/client/send-ussd-modal';
import { toast } from 'sonner';

interface ClientSimCardsProps {
	client: IClient;
	phones: IPhone[] | undefined | null;
	cards: ICard[];
	cardFillJobs?: ICardFillJob[];
	onCopy: (text: string) => void;
}

function retrySimResolve(phone: IPhone, clientId: string) {
	socket.emit(SOCKET_EVENTS.retrySimNumberResolve, {
		phoneId: phone.id,
		clientId,
	});
	toast.message('Повторное определение номера запущено');
}

export default function ClientSimCards({ client, phones, cards, cardFillJobs, onCopy }: ClientSimCardsProps) {
	const [ussdOpen, setUssdOpen] = useState(false);
	const [initialPhoneId, setInitialPhoneId] = useState<number | undefined>(undefined);
	const [detailPhone, setDetailPhone] = useState<IPhone | null>(null);
	const [showMoreUnlinked, setShowMoreUnlinked] = useState(false);

	const phoneList = phones?.filter(p => p) ?? [];
	const phoneGroups = useMemo(() => groupCardsByPhone(cards, phoneList), [cards, phoneList]);
	const unlinked = useMemo(() => splitUnlinkedCards(cards, phoneList), [cards, phoneList]);

	const visibleUnlinked = unlinked.withBalance;
	const hiddenUnlinked = unlinked.withoutBalance;
	const hasUnlinkedSection = visibleUnlinked.length > 0 || hiddenUnlinked.length > 0;

	function openUssdModal(phoneId: number) {
		setInitialPhoneId(phoneId);
		setUssdOpen(true);
	}

	function handleUssdOpenChange(open: boolean) {
		setUssdOpen(open);
		if (!open) setInitialPhoneId(undefined);
	}

	return (
		<div className='flex flex-col gap-2'>
			<div className='flex items-center gap-1 min-w-0'>
				<PhoneIcon size={14} className='opacity-40 shrink-0' />
				<span className='text-sm text-muted-foreground'>
					SIM и карты
					{phoneList.length > 0 ? <span className='tabular-nums'> ({phoneList.length})</span> : null}
				</span>
			</div>

			{phoneList.length ? (
				<div className='flex flex-col gap-2'>
					{phoneGroups.map(({ phone, cards: linkedCards }) => (
						<div
							key={phone.id}
							className={cn(
								'rounded-lg border border-border bg-card/50 p-2 shadow-sm border-l-[3px]',
								getPhoneStatusBorderClass(phone.status),
							)}
						>
							<PhoneSimHeader
								phone={phone}
								clientId={client.id}
								cardFillJobs={cardFillJobs}
								onCopy={onCopy}
								onUssd={() => openUssdModal(phone.id)}
								onInfo={() => setDetailPhone(phone)}
								onRetryResolve={
									phone.status === 3 ? () => retrySimResolve(phone, client.id) : undefined
								}
							/>

							{linkedCards.length ? (
								<div className='mt-2 flex flex-col gap-2'>
									{linkedCards.map(card => (
										<CardTreeBlock
											key={card.id}
											card={card}
											phones={phoneList}
											cardFillJobs={cardFillJobs}
											onCopy={onCopy}
										/>
									))}
								</div>
							) : (
								<EmptyPhoneCardsTree />
							)}
						</div>
					))}
				</div>
			) : (
				<span className='opacity-60 text-sm'>Нет номеров</span>
			)}

			{hasUnlinkedSection ? (
				<div className='flex flex-col gap-1.5 pt-1'>
					<div className='flex items-center gap-1 min-w-0'>
						<CreditCardIcon size={14} className='opacity-40 shrink-0' />
						<span className='text-sm text-muted-foreground'>
							Карты без SIM
							<span className='tabular-nums'>
								{' '}
								({visibleUnlinked.length + hiddenUnlinked.length})
							</span>
						</span>
					</div>

					<div className='flex flex-col gap-2'>
						{visibleUnlinked.map(card => (
							<CardTreeBlock
								key={card.id}
								card={card}
								phones={phoneList}
								cardFillJobs={cardFillJobs}
								onCopy={onCopy}
							/>
						))}

						{hiddenUnlinked.length > 0 ? (
							<>
								{showMoreUnlinked ? (
									hiddenUnlinked.map(card => (
										<CardTreeBlock
											key={card.id}
											card={card}
											phones={phoneList}
											cardFillJobs={cardFillJobs}
											onCopy={onCopy}
										/>
									))
								) : null}
								<Button
									type='button'
									variant='ghost'
									size='sm'
									className='h-7 w-fit px-2 text-xs text-muted-foreground'
									onClick={() => setShowMoreUnlinked(v => !v)}
								>
									{showMoreUnlinked ? (
										<ChevronUpIcon size={14} className='mr-1' />
									) : (
										<ChevronDownIcon size={14} className='mr-1' />
									)}
									{showMoreUnlinked ? 'Скрыть' : `Ещё ${hiddenUnlinked.length}`}
								</Button>
							</>
						) : null}
					</div>
				</div>
			) : null}

			<SendUssdModal
				client={client}
				open={ussdOpen}
				onOpenChange={handleUssdOpenChange}
				initialPhoneId={initialPhoneId}
			/>

			<CardFillDetailDialog
				target={detailPhone ? { mode: 'phone', phone: detailPhone } : null}
				jobs={cardFillJobs}
				open={detailPhone != null}
				onOpenChange={open => {
					if (!open) setDetailPhone(null);
				}}
			/>
		</div>
	);
}
